import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import {
  WorkOrderItemSource,
  type WorkOrderItemSourceValue,
  type WorkOrderItemTypeValue,
} from "features/WorkOrders/constants/work-order-item.constants";
import type { AmcItem, AmcItemPersistPayload } from "features/Amc/types/amc.types";

export function amcLineQuantity(line: AmcItem): number {
  const q: unknown = line.quantity ?? line.qty;
  if (typeof q === "number" && Number.isFinite(q)) return q;
  if (typeof q === "string" && String(q).trim() !== "") {
    const n = Number(q);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function inferSourceType(line: AmcItem): WorkOrderItemSourceValue {
  if (line.sourceType) return line.sourceType;
  const qid = line.quotationItemId;
  if (qid != null) {
    const n = Number(qid);
    if (Number.isFinite(n) && n > 0) return WorkOrderItemSource.QUOTATION;
  }
  return WorkOrderItemSource.ADHOC;
}

/** Form row for `BoqItemsFieldArray` (matches petty work / quotations). */
export type AmcBoqFormRow = {
  type: QuotationItemType;
  description: string;
  lineId?: number;
  onHandQty?: number | null;
  quantity: number;
  unit: string;
  rate: number;
  sourceType: WorkOrderItemSourceValue;
  quotationItemId: number | null;
  originalQuantity: number | null;
};

export function amcItemToBoqRow(line: AmcItem): AmcBoqFormRow {
  const sourceType = inferSourceType(line);
  const qidRaw = line.quotationItemId;
  const quotationItemId =
    qidRaw != null && Number.isFinite(Number(qidRaw)) && Number(qidRaw) > 0
      ? Number(qidRaw)
      : null;

  const qty = amcLineQuantity(line);

  const original =
    line.originalQuantity != null &&
    Number.isFinite(Number(line.originalQuantity))
      ? Number(line.originalQuantity)
      : sourceType === WorkOrderItemSource.QUOTATION
        ? qty
        : null;

  return {
    type: (line.type as QuotationItemType) ?? QuotationItemType.SERVICE,
    description: line.description ?? "",
    lineId:
      line.id != null &&
      Number.isFinite(Number(line.id)) &&
      Number(line.id) > 0
        ? Number(line.id)
        : undefined,
    onHandQty: undefined,
    quantity: qty,
    unit: line.unit ?? "",
    rate: line.rate != null ? Number(line.rate) : 0,
    sourceType,
    quotationItemId,
    originalQuantity: original,
  };
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function boqRowToAmcPersistLine(row: AmcBoqFormRow): AmcItemPersistPayload {
  const qty = Number(row.quantity);
  const type = row.type as WorkOrderItemTypeValue;
  const description = String(row.description ?? "").trim();
  const unit = String(row.unit ?? "").trim();
  const rate = Number(row.rate);

  const sourceType = row.sourceType;
  const quotationItemId =
    sourceType === WorkOrderItemSource.QUOTATION &&
    row.quotationItemId != null &&
    Number.isFinite(Number(row.quotationItemId)) &&
    Number(row.quotationItemId) > 0
      ? Number(row.quotationItemId)
      : null;

  const originalQuantity =
    sourceType === WorkOrderItemSource.QUOTATION &&
    row.originalQuantity != null &&
    Number.isFinite(Number(row.originalQuantity))
      ? Number(row.originalQuantity)
      : null;

  const amount = roundMoney(qty * rate);

  const base: AmcItemPersistPayload = {
    sourceType,
    quotationItemId,
    description,
    type,
    originalQuantity,
    quantity: qty,
    unit,
    rate,
    amount,
  };

  if (
    row.lineId != null &&
    Number.isFinite(Number(row.lineId)) &&
    Number(row.lineId) > 0
  ) {
    return { ...base, id: Number(row.lineId) };
  }

  return base;
}
